<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="app-container">
    <Header
      @handleLoginSelection="handleLoginSelection"
      @handleSignUpSelection="handleSignUpSelection"
    />

    <PhotoBanner
      :imageSrc="require('@/assets/temple.png')"
      title="Live Drone Darshan"
      subheading="connecting hearts to holy places"
      iconType="sunrise"
    />

    <!-- Blog Content -->
    <div :class="mainContentClass">
      <div class="how-it-works-container">
        <div class="title-container">
          <h1 class="main-title">Why Live Drone Darshan?</h1>
        </div>
        <p class="main-subheading">
          Live drone darshan offers a unique and sacred way to connect with
          temples.
        </p>

        <div class="why-drone-mobile-list">
          <div class="why-drone-mobile-card">
            <h3>Can't Always Travel</h3>
            <p>
              Distance, crowds, age, and time make many pilgrimages hard. Live
              darshan brings the temple to you.
            </p>
          </div>
          <div class="why-drone-mobile-card">
            <h3>Stay Close to Tradition</h3>
            <p>
              Join aarti, festivals, and seasonal rituals—even when you're
              overseas.
            </p>
          </div>
          <div class="why-drone-mobile-card">
            <h3>Share the Blessing</h3>
            <p>
              Watch together across time zones; receive a digital prasad or
              blessing note.
            </p>
          </div>
          <div class="why-drone-mobile-card">
            <h3>Accessible</h3>
            <p>Join darshan from anywhere—no travel, no barriers.</p>
          </div>
          <div class="why-drone-mobile-card">
            <h3>Aerial</h3>
            <p>See the temple’s beauty and setting from above.</p>
          </div>
          <div class="why-drone-mobile-card">
            <h3>Connected</h3>
            <p>Share the moment live with family and friends.</p>
          </div>
        </div>
      </div>

      <div class="title-container">
        <h1 class="main-title">Bringing Holy Places to Devotees Home</h1>
      </div>
      <p class="main-subheading">
        Experience live temple views, aarti moments, and sacred
        architecture—without the travel, crowds, or distance.
      </p>
      <br />
      <br />

      <div class="vision-cards">
        <div class="vision-card">
          <vue-feather type="eye" size="18" style="color: black"></vue-feather>
          <h3>See the Temple</h3>
          <p>View sanctums, courtyards, and surroundings in real time.</p>
        </div>

        <div class="vision-card">
          <vue-feather
            type="users"
            size="18"
            style="color: black"
          ></vue-feather>
          <h3>Join the Moment</h3>
          <p>
            Watch aarti or festivals live; chat questions to the guide host.
          </p>
        </div>

        <div class="vision-card">
          <vue-feather
            type="book-open"
            size="18"
            style="color: black"
          ></vue-feather>
          <h3>Learn the Story</h3>
          <p>Hear the history, legends, and why the site is sacred.</p>
        </div>
      </div>

      <br />
      <br />
      <br />

      <div class="title-container">
        <h1 class="main-title">Choose From Our Stunning Locations</h1>
      </div>
      <p class="main-subheading">
        Experience live temple views, aarti moments, and sacred
        architecture—without the travel, crowds, or distance.
      </p>
      <br />
      <div>
        <GridMobile :flights="templeFlights" guestLabel="guest" />
      </div>

      <div class="title-container" style="margin-top: 10rem">
        <h1 class="main-title">How it works</h1>
      </div>
      <p class="main-subheading">
        Setting up a live drone darshan is easy and meaningful. We've got you
        covered.
      </p>
      <br />
      <br />
      <br />

      <div class="how-it-works-container">
        <div class="vision-cards">
          <div class="vision-card">
            <vue-feather
              type="home"
              size="18"
              style="color: black"
            ></vue-feather>
            <h3>Pick a Temple</h3>
            <p>
              Choose from India's most sacred sites and pilgrimage destinations.
            </p>
          </div>
          <br />
          <div class="vision-card">
            <vue-feather
              type="calendar"
              size="18"
              style="color: black"
            ></vue-feather>
            <h3>Book Date / Aarti</h3>
            <p>Select live darshan time or special festival broadcast.</p>
          </div>
          <br />
          <div class="vision-card">
            <vue-feather
              type="play-circle"
              size="18"
              style="color: black"
            ></vue-feather>
            <h3>Join Live Stream</h3>
            <p>Watch the live drone feed and connect with devotees.</p>
          </div>
        </div>
      </div>

      <ContactForm
        id="contact-form"
        :formData="formData"
        :submitForm="submitForm"
        showBanner
        subheading="Get in touch with us to learn more about our drone darshan"
      />
    </div>
    <Footer />

    <SignUpModal
      :isOpen="isSignUpModalOpen"
      @close="closeSignUpModal"
      :onSubmit="handleSignUp"
      @openLoginModal="openLoginModal"
      :errorMessage="errorMessage"
    />

    <LoginModal
      :isOpen="isLoginModalOpen"
      @close="closeLoginModal"
      @openSignUpModal="openSignUpModal"
      :onSubmit="handleLoginButton"
      :errorMessage="errorMessage"
    />
  </div>
</template>

<script>
import { auth } from "@/plugins/firebase";
import { useSessionStore } from "../stores/session.js";
import { isMobile } from "@/utils/helper";
import { computed } from "vue";
import { amplitude } from "../plugins/amplitude.js";
import { loginService, userAuth } from "../services/userAuth";
import Header from "@/components/organisms/Header.vue";
import Footer from "@/components/Footer.vue";
import ContactForm from "@/components/organisms/ContactForm.vue";
import {
  addDoc,
  collection,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import PhotoBanner from "@/components/organisms/PhotoBanner.vue";
import SignUpModal from "@/components/organisms/modal/signUpModal.vue";
import LoginModal from "../components/organisms/modal/loginModal.vue";
import GridMobile from "@/components/GridMobile.vue";

export default {
  name: "TemplesMobileView",
  components: {
    Header,
    Footer,
    ContactForm,
    PhotoBanner,
    SignUpModal,
    LoginModal,
    GridMobile,
  },
  data() {
    return {
      templeFlights: [],
      formData: {
        name: "",
        email: "",
        organization: "",
        phone: "",
        message: "",
        type: "temples",
        createdAt: new Date(),
      },
      isSignUpModalOpen: false,
      isLoginModalOpen: false,
      errorMessage: "",
    };
  },
  computed: {
    isUserAuthenticated() {
      const sessionStore = useSessionStore();
      return sessionStore.userId !== null;
    },
  },
  methods: {
    navigateToListing(listingId) {
      this.$router.push({
        path: `/listing`,
        query: { listingId: listingId },
      });
    },
    fetchTempleFlights() {
      const db = getFirestore();
      const templeFlightsQuery = query(
        collection(db, "listings"),
        orderBy("createdAt", "desc")
      );

      this.unsubscribe = onSnapshot(templeFlightsQuery, (snapshot) => {
        const temples = snapshot.docs.map((doc) => {
          return {
            id: doc.id,
            ...doc.data(),
          };
        });

        this.templeFlights = temples.map((temple) => {
          return {
            ...temple,
            pricePerGuest: 49,
            eventDate: temple.createdAt.toDate().toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            }),
            eventTime: temple.createdAt.toDate().toLocaleTimeString(),
            endDate: new Date(temple.endDate).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            }),
          };
        });
      });
    },
    submitForm() {
      const db = getFirestore();
      const formCollection = collection(db, "inquiries");

      addDoc(formCollection, this.formData)
        .then(() => {
          console.log("Form data submitted successfully!");
          this.formData = {
            name: "",
            email: "",
            organization: "",
            message: "",
            type: "temples",
            createdAt: new Date(),
          };
        })
        .catch((error) => {
          console.error("Error submitting form data: ", error);
        });
    },
    handleLoginSelection() {
      this.openLoginModal();
    },
    handleSignUpSelection() {
      this.openSignUpModal();
    },
    openLoginModal() {
      this.isLoginModalOpen = true;
    },
    closeLoginModal() {
      this.isLoginModalOpen = false;
      this.errorMessage = "";
    },
    openSignUpModal() {
      this.isSignUpModalOpen = true;
    },
    closeSignUpModal() {
      this.isSignUpModalOpen = false;
      this.errorMessage = "";
    },
    async handleLoginButton(data) {
      amplitude.track("Login Form Submitted", {
        type: "login",
        source: "temples_mobile_page",
        email_domain: data.email.split("@")[1] || "unknown",
        timestamp: new Date().toISOString(),
      });

      this.errorMessage = "";

      try {
        const user = await loginService.loginUser(data.email, data.password);
        const sessionStore = useSessionStore();
        sessionStore.setUserId(user.uid);

        amplitude.track("Login Success", {
          userId: user.uid,
          email: user.email,
          source: "temples_mobile_page",
          timestamp: new Date().toISOString(),
        });

        this.closeLoginModal();
      } catch (error) {
        this.errorMessage = error.message;
      }
    },
    async handleSignUp(data) {
      this.errorMessage = "";

      amplitude.track("SignUp Form Submitted", {
        type: "signUp",
        source: "temples_mobile_page",
        timestamp: new Date().toISOString(),
      });

      try {
        const user = await userAuth.signUpUser(
          data.username,
          data.email,
          data.password,
          data.confirmPassword
        );
        const sessionStore = useSessionStore();
        sessionStore.setUserId(user.uid);

        amplitude.track("SignUp Success", {
          userId: user.uid,
          email: user.email,
          displayName: user.displayName || data.username,
          source: "temples_mobile_page",
          timestamp: new Date().toISOString(),
        });

        this.closeSignUpModal();
      } catch (error) {
        this.errorMessage = error.message;
      }
    },
  },
  mounted() {
    const sessionStore = useSessionStore();
    this.fetchTempleFlights();
    auth.onAuthStateChanged((user) => {
      if (user) {
        sessionStore.userId = user.uid;
      } else {
        sessionStore.userId = null;
      }
    });
  },
  setup() {
    const mainContentClass = computed(() => {
      return isMobile() ? "mobile-main-content" : "web-main-content";
    });
    const headerClass = computed(() => {
      return isMobile() ? "mobile-header" : "web-header";
    });

    return {
      mainContentClass,
      headerClass,
    };
  },
};
</script>

<style scoped>
/* General Styles */
.step-list {
  list-style-type: none;
  padding-left: 0;
  margin-bottom: 1rem;
  width: 100%;
}

.step-list-item {
  margin-bottom: 1rem;
  color: #7e7e7e;
}

.step-list-item strong {
  color: #000;
  font-size: 1.5rem;
  font-weight: 400;
}

.nested-list {
  list-style-type: disc;
  padding-left: 1.5rem;
  margin-top: 0.5rem;
}

.step-item-label {
  font-weight: bold;
  color: #aaa;
  font-size: 14px;
  margin-bottom: 1rem;
}

code {
  background-color: #2d2d2d;
  padding: 0.2rem 0.5rem;
  border-radius: 5px;
  color: #000;
}

/* Container and layout */
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--background-color);
  color: var(--text-color);
}

/* Main Content */
.mobile-main-content {
  margin-top: 20px;
  padding: 20px;
}

.web-main-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.title-container {
  position: relative;
  margin-bottom: 1rem;
}

.banner {
  width: 100%;
  height: 85vh;
  object-fit: cover;
  z-index: 0;
}

.small-banner {
  width: 100%;
  height: 10vh;
  object-fit: contain;
}

.overlay-container {
  position: absolute;
  top: 5%;
  left: 5%;
  transform: translateY(0);
  border-radius: 12px;
  width: 80%;
  padding: 2rem;
  text-align: left;
  color: white;
}

.overlay-title {
  font-size: 5rem;
  margin-bottom: 0.5rem;
}

.overlay-subheading {
  font-size: 2rem;
  margin-bottom: 1rem;
  color: #f2f5f9;
}

.overlay-time-container {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.overlay-time {
  font-size: 1rem;
  color: #e6e4e4;
}

.overlay-description-container {
  margin-top: 1rem;
  width: 40%;
  line-height: 1.4rem;
}

.overlay-description {
  font-size: 1rem;
  margin-bottom: 1.5rem;
  color: "white";
}

.overlay-input {
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 200px;
  margin-bottom: 1rem;
}

.overlay-button {
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: 50px;
  border: 1px solid #e5e7eb !important;
  border-radius: 25px;
  cursor: pointer;
  padding: 10px;
  outline: none;
  font-weight: 600;
  transition: background-color 0.3s ease;
}

.overlay-button:hover {
  background-color: #e6e4e4;
}

.flightportal {
  width: 100%;
  height: 30vh;
  object-fit: cover;
  border-radius: 8px;
}

.divider {
  border-bottom: 1px solid #2d2d2d;
}

.main-title {
  font-size: 2rem;
  color: #000;
}

.main-subheading {
  font-size: 1rem;
  color: #464646;
  text-align: left;
}

.avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
}

.overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  text-align: center;
  font-size: 3rem;
  width: 80%;
}

.how-it-works-container {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.contact-container {
  display: flex;
  justify-content: space-between;
  margin-top: 10rem;
  margin-bottom: 10rem;
  gap: 5rem;
  flex-wrap: wrap;
}

.contact-container li {
  margin-bottom: 0.5rem;
  color: #000;
}

.vision-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin: 10rem 0rem;
}

.vision-cards {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  margin-bottom: 3rem;
  gap: 1rem;
}

.vision-card {
  border-radius: 8px;
  width: 100%;
  align-items: left;
  text-align: left;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 0;
  margin: 0;
}

.vision-card h2 {
  font-size: 2rem;
  color: #222;
  text-align: left;
  margin: 0.5rem 0;
}

.vision-card h3 {
  font-size: 1.5rem;
  color: #222;
  text-align: left;
  margin: 0.5rem 0;
}

.vision-card p {
  font-size: 1rem;
  color: #7e7e7e;
  text-align: left;
}

.vision-card span {
  color: #000;
}

.vision-card span:hover {
  text-decoration: underline;
}

.learn-more {
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 5px;
  display: flex;
  cursor: pointer;
}

.card-image {
  width: 100%;
  height: 380px;
  object-fit: cover;
  border-radius: 8px;
}

.card-image1 {
  width: 100%;
  height: 380px;
  object-fit: contain;
  border-radius: 8px;
}

.step-description,
.step-title {
  color: #000;
}

.chip {
  display: inline-block;
  padding: 5px 10px;
  font-size: 0.8rem;
  color: white;
  background-color: #2d2d2d;
  border-radius: 12px;
  margin-top: 5px;
  margin-right: 5px;
}

.date {
  font-size: 1rem;
  color: gray;
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.step-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: 2rem;
  padding: 0;
}

.step-title {
  font-size: 2rem;
  color: #000;
  margin-bottom: 1rem;
}

.sub-step-title {
  font-size: 1.5rem;
  color: #000;
  margin-bottom: 0.5rem;
  margin-top: 1rem;
}

.step-description {
  font-size: 1rem;
  line-height: 1.6;
  color: #000;
}

.sustainable-goals-section {
  text-align: left;
  margin-bottom: 10rem;
}

.benefits-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 2rem 0;
  margin-top: 10rem;
  margin-bottom: 10rem;
}

.benefit-cards {
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.benefit-card {
  border-radius: 8px;
  padding: 1rem;
  text-align: center;
  width: 45%;
  height: 220px;
  background-color: #f2f5f9;
  display: flex;
  flex-direction: column;
  position: relative;
}

.benefit-card h3 {
  font-size: 1.5rem;
  color: #222;
  text-align: center;
  margin-bottom: 0.5rem;
}

.benefit-card p {
  font-size: 0.9rem;
  color: #7e7e7e;
  text-align: center;
  min-height: 40px;
  margin-bottom: 10px;
}

.benefit-card .learn-more-link {
  position: absolute;
  bottom: 15px;
  left: 0;
  right: 0;
}

.benefit-icon {
  margin-top: 1rem;
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.testimonial {
  font-style: italic;
  color: #a8a8a8;
  border-left: 4px solid #2d2d2d;
  padding-left: 1rem;
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.contact-form {
  display: flex;
  flex-direction: column;
}

.contact-form div {
  margin-bottom: 1rem;
}

.contact-form label {
  margin-bottom: 0.5rem;
  font-weight: bold;
  color: #000;
}

.contact-form input,
.contact-form textarea {
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 100%;
}

.contact-form button {
  background-color: #1565c0;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.5rem;
  cursor: pointer;
}

.learn-more-link {
  color: #000;
  text-decoration: underline;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-weight: 500;
  width: 100%;
}
.why-drone-mobile-list {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
  margin: 3rem 0 5rem 0;
  width: 100%;
}
.why-drone-mobile-card {
  text-align: left;
  padding: 0;
}
.why-drone-mobile-card h3 {
  font-size: 1.5rem;
  color: #222;
  margin-bottom: 0.5rem;
}
.why-drone-mobile-card p {
  font-size: 1rem;
  color: #7e7e7e;
  margin: 0;
}
</style>
